import React, { useState, useEffect, useCallback } from "react";
import { Modal, Alert, Button, Badge, Form, Collapse } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faSpinner,
  faCalendarAlt,
} from "@fortawesome/free-solid-svg-icons";
import { Calendar as BigCalendar, momentLocalizer } from "react-big-calendar";
import moment from "moment";
import "moment-timezone";
import "react-big-calendar/lib/css/react-big-calendar.css";
import axios from "axios";

const API_BASE_URL = process.env.REACT_APP_BACKURL || "http://localhost:8000";

// Set up the moment localizer for react-big-calendar
const localizer = momentLocalizer(moment);

// Public Event Details Modal - Read Only
const PublicEventDetailsModal = ({
  show,
  onHide,
  detailedEventData,
  detailLoading,
}) => {
  return (
    <Modal
      show={show}
      onHide={onHide}
      centered
      size="lg"
      style={{ fontFamily: "var(--bs-body-font-family)" }}
    >
      <Modal.Header className="text-white">
        <Modal.Title className="d-flex align-items-center flex-grow-1">
          <h4 className="mb-0">Event Details</h4>
        </Modal.Title>
        <Button
          variant="light"
          onClick={onHide}
        >
          <i className="fa fa-times"></i>
        </Button>
      </Modal.Header>

      <Modal.Body>
        {detailLoading ? (
          <div className="text-center py-5">
            <FontAwesomeIcon
              icon={faSpinner}
              spin
              size="2x"
              className="text-primary"
            />
            <p className="mt-2">Fetching event details...</p>
          </div>
        ) : detailedEventData ? (
          <div className="row">
            <div className="col-12 mb-4">
              <h3 className="text-primary">
                {detailedEventData.title || "No Title"}
              </h3>
            </div>

            <div className="col-lg-6 col-md-6 col-12 mb-3">
              <h5 className="mb-1 text-secondary">
                <i className="bi bi-bank me-2"></i>Hosting Organization
              </h5>
              <p className="lead fw-medium mb-0">
                {detailedEventData.orgName || "N/A"}
              </p>
            </div>

            <div className="col-lg-6 col-md-6 col-12 mb-3">
              <h5 className="mb-1 text-secondary">
                <i className="bi bi-geo-alt-fill me-2"></i>Location
              </h5>
              {(() => {
                const address =
                  detailedEventData.addresses &&
                  detailedEventData.addresses.length > 0
                    ? detailedEventData.addresses[0]
                    : null;

                const formattedLocation = address
                  ? `${address.locationName ? address.locationName + ", " : ""}${address.addressLine1}, ${address.city}, ${address.state} ${address.zip}`
                  : "Not Specified";

                return (
                  <p className="lead fw-medium" style={{ whiteSpace: "pre-wrap" }}>
                    <a
                      href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(formattedLocation)}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-underline-on-hover text-primary"
                    >
                      {formattedLocation} <i className="bi bi-box-arrow-up-right"></i>
                    </a>
                  </p>
                );
              })()}
            </div>

            <div className="col-lg-6 col-md-6 col-12 mb-3">
              <h5 className="mb-1 text-secondary">
                <FontAwesomeIcon icon={faCalendarAlt} className="me-2" />
                Starts (Your Local Time)
              </h5>
              <p className="lead fw-medium text-success">
                {(() => {
                  const schedule =
                    detailedEventData.schedule &&
                    detailedEventData.schedule.length > 0
                      ? detailedEventData.schedule[0]
                      : null;
                  if (!schedule) return "N/A";

                  const eventStartUTC = moment.tz(
                    `${schedule.effectiveFrom} ${schedule.startTime}`,
                    "YYYY-MM-DD HH:mm:ss",
                    schedule.timeZone
                  );

                  const localStart = eventStartUTC.local();
                  const userTimeZoneName = moment.tz.guess();

                  const formattedDate = localStart.format("MMM D, YYYY");
                  const formattedTime = localStart.format("h:mm A");

                  return `${formattedDate} at ${formattedTime} (${userTimeZoneName})`;
                })()}
              </p>
            </div>

            <div className="col-lg-6 col-md-6 col-12 mb-3">
              <h5 className="mb-1 text-secondary">
                <i className="bi bi-calendar-x-fill me-2"></i>
                Ends (Your Local Time)
              </h5>
              <p className="lead fw-medium text-danger">
                {(() => {
                  const schedule =
                    detailedEventData.schedule &&
                    detailedEventData.schedule.length > 0
                      ? detailedEventData.schedule[0]
                      : null;

                  if (!schedule) return "N/A";

                  const eventEndUTC = moment.tz(
                    `${schedule.effectiveTo} ${schedule.endTime}`,
                    "YYYY-MM-DD HH:mm:ss",
                    schedule.timeZone
                  );

                  const localEnd = eventEndUTC.local();
                  const userTimeZoneName = moment.tz.guess();

                  const formattedDate = localEnd.format("MMM D, YYYY");
                  const formattedTime = localEnd.format("h:mm A");

                  return `${formattedDate} at ${formattedTime} (${userTimeZoneName})`;
                })()}
              </p>
            </div>

            <div className="col-12 mt-3 pt-3 border-top">
              <h5 className="mb-2">
                <i className="bi bi-card-checklist me-2"></i>Services Offered
              </h5>
              {detailedEventData.serviceTypes &&
              detailedEventData.serviceTypes.length > 0 ? (
                <div className="d-flex flex-wrap gap-2">
                  {detailedEventData.serviceTypes.map((service) => (
                    <span
                      key={service.serviceTypeID}
                      className="badge bg-primary fs-6 px-3 py-2"
                    >
                      {service.name}
                    </span>
                  ))}
                </div>
              ) : (
                <p className="text-muted fst-italic">
                  No services specified for this event.
                </p>
              )}
            </div>

            <div className="col-12 mt-3 pt-3 border-top">
              <h5 className="mb-2">
                <i className="bi bi-file-text-fill me-2"></i>Description
              </h5>
              <p className="text-wrap lead fw-medium">
                {detailedEventData.description || (
                  <em>No description provided for this event.</em>
                )}
              </p>
            </div>
          </div>
        ) : (
          <Alert variant="info" className="text-center">
            Select an event to see its details.
          </Alert>
        )}
      </Modal.Body>
    </Modal>
  );
};

// Main Public Events Calendar Component
const PublicEventsCalendar = () => {
  // State for events
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // State for filters
  const [filterCriteria, setFilterCriteria] = useState({
    title: "",
    startDate: "",
    endDate: "",
    city: "",
  });
  const [openFilter, setOpenFilter] = useState(false);
  const [isFiltered, setIsFiltered] = useState(false);

  // State for calendar control
  const [currentView, setCurrentView] = useState("month");
  const [currentDate, setCurrentDate] = useState(new Date());

  // State for Event Detail Modal
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [detailedEventData, setDetailedEventData] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);

  // Build query string for API call
  const buildQueryString = useCallback((criteria) => {
    const params = new URLSearchParams();

    if (criteria.title) params.append("title", criteria.title);
    if (criteria.city) params.append("city", criteria.city);
    if (criteria.startDate) params.append("startDate", criteria.startDate);
    if (criteria.endDate) params.append("endDate", criteria.endDate);

    return params.toString();
  }, []);

  // Fetch events from API (public endpoint - no auth required)
  const fetchEvents = useCallback(
    async (criteria = {}, isFilterSearch = false) => {
      setLoading(true);
      setError(null);

      const queryString = buildQueryString(criteria);

      try {
        const response = await axios.get(
          `${API_BASE_URL}/api/foodPantry/event/public${queryString ? `?${queryString}` : ""}`
        );

        const formattedEvents = response.data.map((event) => {
          let startDate = new Date(event.effectiveFrom);
          let endDate = new Date(event.effectiveTo);

          if (event.startTime && event.endTime) {
            const dateFrom = moment.utc(event.effectiveFrom).format("YYYY-MM-DD");
            const dateTo = moment.utc(event.effectiveTo).format("YYYY-MM-DD");
            const timeStart = event.startTime;
            const timeEnd = event.endTime;
            const eventTimeZone = event.timeZone || "UTC";

            const combinedStart = `${dateFrom} ${timeStart}`;
            const combinedEnd = `${dateTo} ${timeEnd}`;

            startDate = moment
              .tz(combinedStart, "YYYY-MM-DD HH:mm:ss", eventTimeZone)
              .toDate();
            endDate = moment
              .tz(combinedEnd, "YYYY-MM-DD HH:mm:ss", eventTimeZone)
              .toDate();
          }

          return {
            ...event,
            id: event.eventID,
            title: event.title,
            start: startDate,
            end: endDate,
            allDay: false,
          };
        });

        setEvents(formattedEvents);
        setIsFiltered(isFilterSearch);
      } catch (err) {
        console.error("Failed to fetch events:", err);
        setError(
          "Could not load calendar events. Please try again later."
        );
      } finally {
        setLoading(false);
      }
    },
    [buildQueryString]
  );

  // Fetch event details (public endpoint - no auth required)
  const fetchEventDetails = useCallback(async (eventId) => {
    setDetailLoading(true);
    setDetailedEventData(null);

    try {
      const response = await axios.get(
        `${API_BASE_URL}/api/foodPantry/event/public/${eventId}`
      );
      setDetailedEventData(response.data);
    } catch (err) {
      console.error("Failed to fetch event details:", err);
    } finally {
      setDetailLoading(false);
    }
  }, []);

  // Initial data load
  useEffect(() => {
    fetchEvents();
  }, [fetchEvents]);

  // Event handlers
  const handleSelectEvent = useCallback(
    (event) => {
      setShowDetailModal(true);
      fetchEventDetails(event.id);
    },
    [fetchEventDetails]
  );

  const handleCloseDetailModal = () => {
    setShowDetailModal(false);
    setDetailedEventData(null);
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilterCriteria((prev) => ({ ...prev, [name]: value }));
  };

  const handleApplyFilters = (e) => {
    e.preventDefault();
    fetchEvents(filterCriteria, true);
  };

  const handleClearFilters = () => {
    setFilterCriteria({
      title: "",
      startDate: "",
      endDate: "",
      city: "",
    });
    fetchEvents({}, false);
  };

  // Calendar event styling
  const eventPropGetter = useCallback(
    (event, start, end, isSelected) => ({
      style: { cursor: "pointer" },
    }),
    []
  );

  // Custom event components
  const AgendaEvent = ({ event }) => (
    <div style={{ fontWeight: "bold" }}>{event.title}</div>
  );

  const Event = ({ event }) => (
    <div className="rounded-sm">
      <strong style={{ fontSize: "0.8rem" }}>{event.title}</strong>
    </div>
  );

  // Custom toolbar
  const CustomToolbar = (toolbar) => {
    const goToBack = () => toolbar.onNavigate("PREV");
    const goToNext = () => toolbar.onNavigate("NEXT");
    const goToCurrent = () => toolbar.onNavigate("TODAY");

    const label = () => {
      const date = moment(toolbar.date);
      let formattedLabel;

      if (toolbar.view === "day") {
        formattedLabel = date.format("dddd, MMMM D, YYYY");
      } else if (toolbar.view === "week") {
        const startOfWeek = moment(toolbar.date).startOf("week");
        const endOfWeek = moment(toolbar.date).endOf("week");
        const isSameYear = startOfWeek.year() === endOfWeek.year();
        const startFormat = isSameYear ? "MMM D" : "MMM D, YYYY";
        formattedLabel = `${startOfWeek.format(startFormat)} - ${endOfWeek.format("MMM D, YYYY")}`;
      } else if (toolbar.view === "agenda") {
        const startDate = moment(toolbar.date).startOf("day");
        const endDate = moment(toolbar.date).add(30, "days").endOf("day");
        const isSameYear = startDate.year() === endDate.year();
        const startFormat = isSameYear ? "MMM D" : "MMM D, YYYY";
        formattedLabel = `${startDate.format(startFormat)} - ${endDate.format("MMM D, YYYY")}`;
      } else {
        formattedLabel = date.format("MMMM YYYY");
      }

      return (
        <h4 className="mb-0 text-primary">
          <i className="bi bi-calendar-check-fill me-2"></i>
          {formattedLabel}
        </h4>
      );
    };

    const setView = (view) => toolbar.onView(view);

    return (
      <div className="rbc-toolbar row mb-3 align-items-center">
        <div className="col-auto">
          <Button variant="primary" onClick={goToBack} className="me-2" size="sm">
            <i className="bi bi-chevron-left"></i>
          </Button>
          <Button variant="outline-primary" onClick={goToCurrent} className="me-2" size="sm">
            Today
          </Button>
          <Button variant="primary" onClick={goToNext} size="sm">
            <i className="bi bi-chevron-right"></i>
          </Button>
        </div>

        <div className="col text-center">{label()}</div>

        <div className="col-auto d-flex justify-content-end">
          <Button
            variant={toolbar.view === "month" ? "primary" : "outline-primary"}
            onClick={() => setView("month")}
            className="rounded-end-0"
            size="sm"
          >
            Month
          </Button>
          <Button
            variant={toolbar.view === "week" ? "primary" : "outline-primary"}
            onClick={() => setView("week")}
            className="rounded-0 border-start-0 border-end-0"
            size="sm"
          >
            Week
          </Button>
          <Button
            variant={toolbar.view === "day" ? "primary" : "outline-primary"}
            onClick={() => setView("day")}
            className="rounded-start-0"
            size="sm"
          >
            Day
          </Button>
          <Button
            variant={toolbar.view === "agenda" ? "primary" : "outline-primary"}
            onClick={() => setView("agenda")}
            className="rounded-start-0 border-start-0"
            size="sm"
          >
            Agenda
          </Button>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div
        className="d-flex justify-content-center align-items-center"
        style={{ minHeight: "400px" }}
      >
        <FontAwesomeIcon icon={faSpinner} spin size="3x" className="text-primary" />
        <span className="ms-3 h4">Loading Events...</span>
      </div>
    );
  }

  return (
    <div className="container-fluid px-3">
      {error && <Alert variant="danger">{error}</Alert>}

      {/* Header with Filter button */}
      <div className="d-flex justify-content-between align-items-center mb-3 p-3 bg-white rounded shadow-sm">
        <h4 className="m-0">
          <i className="bi bi-calendar-event me-2 text-primary"></i>
          Community Events Calendar
        </h4>
        <div className="d-flex align-items-center gap-3">
          {isFiltered && (
            <Badge pill bg="success" className="p-2">
              <i className="bi bi-funnel-fill me-1"></i>Filters Active
            </Badge>
          )}
          <Button
            onClick={() => setOpenFilter(!openFilter)}
            aria-controls="filterCollapse"
            aria-expanded={openFilter}
            variant="outline-secondary"
            size="sm"
          >
            <i className="bi bi-sliders me-2"></i>
            {openFilter ? "Hide Filters" : "Show Filters"}
          </Button>
        </div>
      </div>

      {/* Filter Section */}
      <Collapse in={openFilter}>
        <div
          id="filterCollapse"
          className="p-3 mb-3 bg-light rounded shadow-sm border"
        >
          <Form onSubmit={handleApplyFilters}>
            <div className="row g-3 mb-3">
              <div className="col-md-6">
                <Form.Group className="form-floating">
                  <Form.Control
                    type="text"
                    id="title"
                    name="title"
                    value={filterCriteria.title}
                    onChange={handleFilterChange}
                    placeholder="Enter event title"
                    style={{ minHeight: "57px" }}
                  />
                  <Form.Label htmlFor="title">Event Title</Form.Label>
                </Form.Group>
              </div>
              <div className="col-md-6">
                <Form.Group className="form-floating">
                  <Form.Control
                    type="text"
                    id="city"
                    name="city"
                    value={filterCriteria.city}
                    onChange={handleFilterChange}
                    placeholder="e.g., Cookeville"
                    style={{ minHeight: "57px" }}
                  />
                  <Form.Label htmlFor="city">City</Form.Label>
                </Form.Group>
              </div>
            </div>

            <div className="row g-3 mb-3">
              <div className="col-md-6">
                <Form.Group className="form-floating">
                  <Form.Control
                    type="date"
                    id="startDate"
                    name="startDate"
                    value={filterCriteria.startDate}
                    onChange={handleFilterChange}
                    placeholder="YYYY-MM-DD"
                  />
                  <Form.Label htmlFor="startDate">Start Date</Form.Label>
                </Form.Group>
              </div>
              <div className="col-md-6">
                <Form.Group className="form-floating">
                  <Form.Control
                    type="date"
                    id="endDate"
                    name="endDate"
                    value={filterCriteria.endDate}
                    onChange={handleFilterChange}
                    placeholder="YYYY-MM-DD"
                  />
                  <Form.Label htmlFor="endDate">End Date</Form.Label>
                </Form.Group>
              </div>
            </div>

            <div className="d-flex justify-content-end gap-2">
              <Button
                variant="secondary"
                onClick={handleClearFilters}
                type="button"
              >
                <i className="bi bi-x-circle me-1"></i>Clear Filters
              </Button>
              <Button variant="primary" type="submit">
                <i className="bi bi-search me-1"></i>Apply Filters
              </Button>
            </div>
          </Form>
        </div>
      </Collapse>

      {/* Calendar */}
      <div
        style={{ height: "70vh" }}
        className="shadow-lg p-3 bg-white rounded"
      >
        <BigCalendar
          localizer={localizer}
          events={events}
          date={currentDate}
          view={currentView}
          onNavigate={setCurrentDate}
          onView={setCurrentView}
          dayLayoutAlgorithm="no-overlap"
          startAccessor="start"
          endAccessor="end"
          titleAccessor="title"
          style={{ minHeight: "100%" }}
          views={["month", "week", "day", "agenda"]}
          onSelectEvent={handleSelectEvent}
          showMoreThreshold={5}
          eventPropGetter={eventPropGetter}
          components={{
            toolbar: CustomToolbar,
            event: Event,
            agenda: { event: AgendaEvent },
          }}
          className="cursor-pointer"
        />
      </div>

      {/* Event Details Modal */}
      <PublicEventDetailsModal
        show={showDetailModal}
        onHide={handleCloseDetailModal}
        detailedEventData={detailedEventData}
        detailLoading={detailLoading}
      />
    </div>
  );
};

export default PublicEventsCalendar;
